/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable } from 'type-fest';
import type { ResultItem } from '@/utils/file';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as JobsAirRoadSearchFormInitialData,
  ValidData as JobsAirRoadSearchFormValidData,
} from '@/components/searchForms/JobsAirRoadSearchForm.vue';
import type { JobsAirRoadViewDataComposable } from '@/composables/data/useJobsAirRoadViewData';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';

import { computed, reactive, toRefs, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineStore } from 'pinia';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import useDownloadCsv from '@/composables/useDownloadCsv';
import * as locationQueryHelpers from '@/helpers/locationQuery';
import * as jobsAirRoadTableHelpers from '@/helpers/tables/jobsAirRoad';
import * as oneTrackingJobHelpers from '@/helpers/trackingJob/oneTrackingJob';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateJobsAirRoadViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<JobsAirRoadViewDataComposable>>>
  >;
  query: RequiredKeys<{
    curPage: number | undefined;
    pageSize: number | undefined;
    dates: [Date, Date] | undefined;
    customer: string | undefined;
    states: string[] | undefined;
    search: string | undefined;
    only920Driver: boolean | undefined;
    exclude920Driver: boolean | undefined;
    fromWarehouse: boolean | undefined;
    toWarehouse: boolean | undefined;
  }>;
}

interface DefJobsAirRoadViewState {
  selectedResultId: number;
}

const defPrivateJobsAirRoadViewState: DefPrivateJobsAirRoadViewState = {
  data: {
    isFetchingAirRoadBookingJobsData: undefined,
    airRoadBookingJobsData: undefined,
    airRoadBookingJobsDataError: undefined,
    refreshAirRoadBookingJobsData: undefined,
    reload: undefined,
  },
  query: {
    curPage: undefined,
    pageSize: undefined,
    dates: undefined,
    customer: undefined,
    states: undefined,
    search: undefined,
    only920Driver: undefined,
    exclude920Driver: undefined,
    fromWarehouse: undefined,
    toWarehouse: undefined,
  },
};

const defJobsAirRoadViewState: DefJobsAirRoadViewState = {
  selectedResultId: NaN,
};

function privateJobsAirRoadViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(
    _cloneDeep(defPrivateJobsAirRoadViewState),
  ) as DefPrivateJobsAirRoadViewState;
  const route = useRoute();

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateJobsAirRoadViewState));
    if (route.name === routeNames.AIR_ROAD_JOBS) {
      state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: JobsAirRoadViewDataComposable) {
    state.data = reactive(viewData);
  }

  function setQuery(routeQuery: CommonRouteQueryComposable) {
    state.query = reactive({
      curPage: routeQuery.queryCurPage,
      pageSize: routeQuery.queryPageSize,
      dates: routeQuery.queryDates,
      customer: routeQuery.queryCustomer,
      states: routeQuery.queryStates,
      search: routeQuery.querySearch,
      only920Driver: routeQuery.queryOnly920Driver,
      exclude920Driver: routeQuery.queryExclude920Driver,
      fromWarehouse: routeQuery.queryFromWarehouse,
      toWarehouse: routeQuery.queryToWarehouse,
    });
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.AIR_ROAD_JOBS) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
    setQuery,
  };
}

function jobsAirRoadViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defJobsAirRoadViewState));
  const route = useRoute();
  const router = useRouter();

  const _jobsAirRoadViewStore = usePrivateJobsAirRoadViewStore();

  const data = computed(function () {
    return _jobsAirRoadViewStore.data;
  });

  const query = computed(function () {
    return _jobsAirRoadViewStore.query;
  });

  const jobsAirRoadTableData = computed(function () {
    return jobsAirRoadTableHelpers.getTableData(
      data.value.airRoadBookingJobsData,
    );
  });

  const initialData = computed<JobsAirRoadSearchFormInitialData>(() => {
    return {
      dates: query.value.dates,
      customer: query.value.customer,
      states: query.value.states,
      search: query.value.search,
      isOnly920Driver: query.value.only920Driver,
      isExclude920Driver: query.value.exclude920Driver,
      isFromWarehouse: query.value.fromWarehouse,
      isToWarehouse: query.value.toWarehouse,
    };
  });

  const selectedJobIdAndJobDate = computed(function () {
    const results = data.value.airRoadBookingJobsData?.results;
    if (!results?.length) return;
    if (Number.isNaN(state.selectedResultId)) return;
    const bookingArJob = results.find(
      (_bookingArJob) => _bookingArJob.id === state.selectedResultId,
    );
    if (!bookingArJob) return;
    return oneTrackingJobHelpers.getJobIdAndJobDate(bookingArJob);
  });

  const { isDownloadingCSV, downloadCsv } = useDownloadCsv();

  const resultsForCsv = computed<ResultItem[]>(() => {
    const results = data.value.airRoadBookingJobsData?.results;
    if (!Array.isArray(results)) return [];
    return results.map((item) => {
      const { ar_jobs: arJobs, stops, ...resultItem } = item;
      return resultItem;
    });
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defJobsAirRoadViewState));
    _jobsAirRoadViewStore.$reset();
  }

  function init(
    viewData: JobsAirRoadViewDataComposable,
    routeQuery: CommonRouteQueryComposable,
  ) {
    _jobsAirRoadViewStore.setData(viewData);
    _jobsAirRoadViewStore.setQuery(routeQuery);
  }

  function search(formData: JobsAirRoadSearchFormValidData): void {
    const query = locationQueryHelpers.buildJobsAirRoadSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.AIR_ROAD_JOBS,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function downloadCSV() {
    downloadCsv(resultsForCsv.value, 'bookingAirRoadJobs');
  }

  watch(
    () => data.value.airRoadBookingJobsData,
    function () {
      state.selectedResultId = NaN;
    },
  );

  return {
    ...toRefs(state),
    data,
    query,
    jobsAirRoadTableData,
    initialData,
    selectedJobIdAndJobDate,
    isDownloadingCSV,
    $reset,
    init,
    search,
    downloadCSV,
  };
}

const usePrivateJobsAirRoadViewStore = defineStore(
  '_jobsAirRoadView',
  privateJobsAirRoadViewStoreSetup,
);

export const useJobsAirRoadViewStore = defineStore(
  'jobsAirRoadView',
  jobsAirRoadViewStoreSetup,
);
